/**
 * shared/constants.ts — every tunable number used by more than one module.
 *
 * OWNER: integration (contract). Change requests: docs/CONTRACT-REQUESTS.md.
 *
 * Source tags (see research/*.md for the full citations):
 *   [NET §n]  research/netcode.md section n        [PM §n]  research/pmove-t6mp.md section n
 *   [WPN]     research/weapons-mp.md / data/weapons_mp.json
 *   [SAS]     research/sticks-and-stones.md / data/sns_rules.json
 *   [CTL]     research/controller.md               [MOVPUB] research/movement-public.md
 *   ESTIMATE  inferred, not read from BO2 data. Replace when a runtime dvardump exists (PM §6).
 *
 * UNITS: distances in inches (1 CoD unit = 1 inch), times in integer milliseconds unless the
 * name ends in _S (seconds), angles in degrees. Coordinates are CoD Z-up (see shared/math.ts).
 */

// ---------------------------------------------------------------------------------------------
// Protocol / timing  [NET §4, §11]
// ---------------------------------------------------------------------------------------------
export const PROTOCOL_VERSION = 1;
/** sv_fps 20 [Q3 sv_init.c; CoD4X sv_main.c:3122; BO2 GSC `*20`, `wait 0.05`]. */
export const SERVER_TICK_HZ = 20;
export const SERVER_TICK_MS = 1000 / SERVER_TICK_HZ; // 50
/** snaps 20 [Q3 cl_main.c], one snapshot per tick. */
export const SNAPSHOT_HZ = 20;
/**
 * Usercmd rate. ESTIMATE: console BO2 runs 60 fps, one usercmd per frame [NET §4, PM §8.2].
 * COMMAND TIMING (decided by foundation, documented in docs/ARCHITECTURE.md §4):
 *   cmd number n (0-based, per connection) has serverTime = cmdTimeBase + floor(n * 1000 / 60)
 *   so msec = serverTime(n) - serverTime(n-1) alternates 16,17,17,16,17,17,... (avg 16.667) exactly
 *   like a 60 fps console. Pmove integrates the integer msec (frametime = msec * 0.001, PM §2).
 */
export const CMD_HZ = 60;
/** Commands per server tick (bots generate exactly this many per tick) [NET §5.2]. */
export const CMDS_PER_TICK = CMD_HZ / SERVER_TICK_HZ; // 3
/** One packet per command. Q3 default cl_maxpackets 30 (15..125); ours 60 [NET §4]. */
export const CMD_SEND_HZ = 60;
/** Redundant old cmds per packet on unreliable transports. Q3 cl_packetdup 1 (0..5). WS = 0. */
export const CMD_REDUNDANCY_UNRELIABLE = 2;
/** Q3 MAX_PACKET_USERCMDS. */
export const MAX_CMDS_PER_PACKET = 32;
/** Q3 PACKET_BACKUP. */
export const SNAPSHOT_BACKUP = 32;
/** Client catches up at most this many cmds per render frame; beyond that the time is dropped [NET §7.2]. */
export const MAX_CMD_CATCHUP_PER_FRAME = 4;

/** serverTime of command `n` relative to cmdTimeBase (integer ms). See CMD_HZ. */
export function cmdTimeOffset(n: number): number {
  return Math.floor((n * 1000) / CMD_HZ);
}

// Antilag [NET §1.6, §8.1]
/** CoD4X g_weapon.c G_AntiLagRewindClientPos: rewind capped at 400 ms. */
export const ANTILAG_MAX_MS = 400;
/** CoD4X: rewind only if lag > one server frame. */
export const ANTILAG_MIN_MS = SERVER_TICK_MS;
/** CoD4X G_SetLastServerTime: viewTime may be at most 1000 ms old. */
export const VIEWTIME_MAX_AGE_MS = 1000;
/** Antilag history ring: 1 s at 20 Hz. */
export const ANTILAG_HISTORY_FRAMES = 20;
/** ESTIMATE of BO2 useAntiLagRewind=1 on projectiles: "full" = rewind whole flight, "spawn" = catch-up only. */
export const PROJECTILE_ANTILAG: 'full' | 'spawn' = 'full';
/** CoD4X SV_ClientThink: reject cmds this far in the future. */
export const CMD_FUTURE_LIMIT_MS = 20000;

// Client clock [NET §7.1] — Q3 CL_AdjustTimeDelta
export const CLOCK_RESET_MS = 500;
export const CLOCK_FAST_MS = 100;
export const CLOCK_DRIFT_UP = 1;
export const CLOCK_DRIFT_DOWN = 2;
/** Q3 cl_timeNudge clamp. */
export const TIME_NUDGE_MIN = -30;
export const TIME_NUDGE_MAX = 30;
/** ESTIMATE: freeze remote entities after extrapolating this long. */
export const EXTRAPOLATE_MAX_MS = 100;
/** Q3 cg_errordecay. */
export const PRED_ERROR_DECAY_MS = 100;
/** ESTIMATE: prediction errors larger than this snap (Q3 "PredictionTeleport"). */
export const PRED_TELEPORT_DIST = 64;
/** ESTIMATE: speed-hack guard slack [NET §5.3]. */
export const CMD_BUDGET_SLACK_MS = 250;
/** ESTIMATE (EF_CONNECTION pattern): show "connection interrupted" after this long without cmds. */
export const CONNECTION_INTERRUPTED_MS = 1000;
/** ESTIMATE: 1/8 unit quantization of remote entity positions, relative to map centre [NET §6.2]. */
export const POS_SCALE = 8;
/** ESTIMATE: reconnect slot kept for 30 s [NET §10]. */
export const RECONNECT_GRACE_MS = 30000;
/** Lobby: max players per room (BO2 wager lobbies were 6-8; we allow 8). */
export const MAX_CLIENTS = 8;
/** Entity numbers: 0..MAX_CLIENTS-1 are players; missiles etc. start at MAX_CLIENTS. */
export const ENTITYNUM_NONE = 0x3ff; // PM §1: groundEntityNum 0x3ff = none
export const ENTITYNUM_WORLD = 0x3fe;
export const MAX_GENTITIES = 1024;
/** Room codes: 4 letters, no I/O [NET §10]. */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 4;
/** Default dev ports. Vite dev server proxies /ws to the game server. */
export const DEFAULT_SERVER_PORT = 8080;
export const DEFAULT_VITE_PORT = 5173;

// ---------------------------------------------------------------------------------------------
// Player movement [PM §4 constants table; ESTIMATE values from PM §6 until a dvardump exists]
// ---------------------------------------------------------------------------------------------
/** PmoveSingle 0x947ed0: mins (-15,-15,0), maxs (15,15,70). */
export const PLAYER_MINS: readonly [number, number, number] = [-15, -15, 0];
export const PLAYER_MAXS_STAND: readonly [number, number, number] = [15, 15, 70];
/** @0xbcd654 */
export const PLAYER_HEIGHT_CROUCH = 50;
/** @0xc21ed4 */
export const PLAYER_HEIGHT_PRONE = 30;
/** Eye heights: prone 11, crouch 40 (ints in 0x941af0); stand = player_standingViewHeight ESTIMATE 60. */
export const VIEWHEIGHT_PRONE = 11;
export const VIEWHEIGHT_CROUCH = 40;
export const VIEWHEIGHT_STAND = 60; // ESTIMATE (hidden dvar @0x322e824)
/** Stand<->crouch view height lerp speed, units/s (@0xbe400c). */
export const VIEWHEIGHT_LERP_SPEED = 180;
/** pmove chunking: Pmove 0x48a040 splits into <=66 ms chunks, max 150 ms catch-up; PmoveSingle clamps 1..200. */
export const PMOVE_MAX_CHUNK_MS = 66;
export const PMOVE_MAX_CATCHUP_MS = 150;
export const PMOVE_MSEC_MIN = 1;
export const PMOVE_MSEC_MAX = 200;
/** g_speed ESTIMATE 190 (hidden). Written into ps.speed by the server. */
export const G_SPEED = 190;
/** bg_gravity / g_gravity ESTIMATE 800 (hidden; GSC reads getdvarfloat("bg_gravity")). ps.gravity. */
export const G_GRAVITY = 800;
/** PM_Friction: friction 5.5 (0x5a9340 -> @0xbd9de4). */
export const PM_FRICTION = 5.5;
/** stopspeed ESTIMATE 100 (hidden dvar @0x322c630). Also the accel floor in PM_Accelerate. */
export const PM_STOPSPEED = 100;
/** Ground accel stand / crouch / prone (WalkMove @0xc8bcbc / @0xc4995c / @0xc8bcb4). */
export const PM_ACCEL_STAND = 9;
export const PM_ACCEL_CROUCH = 12;
export const PM_ACCEL_PRONE = 19;
/** Slick / slide accel (@0xd30a50). */
export const PM_ACCEL_SLICK = 2;
/** Air accel (AirMove 0x9472c0). */
export const PM_AIR_ACCEL = 1;
/** Stance speed scales (0x943ca0). */
export const PM_STANCE_SCALE_CROUCH = 0.65;
export const PM_STANCE_SCALE_PRONE = 0.15;
/** Prone transition blend durations ms (0x258 / 0x190 / 0xc8). */
export const PM_PRONE_DOWN_MS = 600;
export const PM_PRONE_UP_MS = 400;
export const PM_PRONE_UP_FAST_MS = 200;
/** ADS / lean move scale (@0xbd13d8). */
export const PM_ADS_MOVE_SCALE = 0.4;
/** player_backSpeedScale / player_strafeSpeedScale ESTIMATE 0.7 / 0.8 (hidden). */
export const PM_BACK_SPEED_SCALE = 0.7;
export const PM_STRAFE_SPEED_SCALE = 0.8;
/** Sprint speed scale (0x4bf080 -> @0xc41b58) and sprint strafe scale (0x665940 -> @0xc22528). */
export const PM_SPRINT_SPEED_SCALE = 1.5;
export const PM_SPRINT_STRAFE_SCALE = 0.667;
/** Sprint may only start with > 1 s remaining (0x6b56a0). */
export const PM_SPRINT_MIN_START_MS = 1000;
/** player_sprintForwardMinimum ESTIMATE 105 (hidden dvar @0x322c620). */
export const PM_SPRINT_FORWARD_MIN = 105;
/** Jump sprint end delay +800 ms (0x9452f0). */
export const PM_SPRINT_JUMP_END_DELAY_MS = 800;
/**
 * Sprint duration base for SnS: `_teams.gsc set_player_model()` -> setsprintduration(4),
 * setsprintcooldown(0) [WPN §1]; playerSprintTime 4 s [SAS §1]. Written into ps.sprint.durationBase.
 */
export const SNS_SPRINT_DURATION_MS = 4000;
export const SNS_SPRINT_COOLDOWN_MS = 0;
/** Jump: height 39 (@0xc31314), cooldown 500 ms, v = sqrt(2*g*39) = 249.8 u/s. */
export const PM_JUMP_HEIGHT = 39;
export const PM_JUMP_COOLDOWN_MS = 500;
/** jump_stepSize 18, jump_slowdownEnable 1, jump_ladderPushVel 128, jump_spreadAdd 64 (0x6e61b0). */
export const PM_JUMP_STEP_SIZE = 18;
export const PM_JUMP_SLOWDOWN_ENABLE = true;
export const PM_JUMP_LADDER_PUSH_VEL = 128;
export const PM_JUMP_SPREAD_ADD = 64;
/** Land slowdown: x0.65 (x0.5 if landed >= 18 above takeoff), pm_time 1800 / 1200 (0x454330). */
export const PM_LAND_SCALE = 0.65;
export const PM_LAND_SCALE_HIGHER = 0.5;
export const PM_LAND_TIME_MS = 1800;
export const PM_LAND_TIME_HIGHER_MS = 1200;
/** Post-land friction 1 + pm_time*1.5/1700, cap 2.5 (0x6350b0). */
export const PM_LAND_FRICTION_MAX = 2.5;
/** Step size 18 (prone 10); ground snap 9; step-up retried on slopes with normal.z < 0.9 (0x442090). */
export const PM_STEP_SIZE = 18;
export const PM_STEP_SIZE_PRONE = 10;
export const PM_STEP_DOWN_SNAP = 9;
export const PM_STEP_SLOPE_NORMAL_Z = 0.9;
/** Walkable slope: IW/Q3 MIN_WALK_NORMAL 0.7. ESTIMATE (PM §9: GroundTrace threshold unverified). */
export const PM_MIN_WALK_NORMAL = 0.7;
/** ClipVelocity overbounce (AirMove). */
export const PM_OVERCLIP = 1.001;
/** Footstep bob table BOBMOVE[stanceIdx][slowIdx] @0xc8beac (stand, prone, crouch, stand-back, prone-back, crouch-back). */
export const PM_BOBMOVE: readonly (readonly [number, number])[] = [
  [0.335, 0.28], [0.25, 0.24], [0.34, 0.29], [0.36, 0.3], [0.25, 0.24], [0.34, 0.29],
];
/** Mantle dvars (dvars_t6mp.json, 0x5b1540). */
export const MANTLE_CHECK_RANGE = 20;
/** Fall damage: hidden dvars; IW defaults bg_fallDamageMinHeight 128 / MaxHeight 300. ESTIMATE. */
export const FALL_DAMAGE_MIN_HEIGHT = 128;
export const FALL_DAMAGE_MAX_HEIGHT = 300;

// ---------------------------------------------------------------------------------------------
// Weapons / combat [WPN]
// ---------------------------------------------------------------------------------------------
/** T5 bg_misc.cpp player_meleeRange 64 / Width 10 / Height 10. ESTIMATE for T6 [WPN §5]. */
export const PLAYER_MELEE_RANGE = 64;
export const PLAYER_MELEE_WIDTH = 10;
export const PLAYER_MELEE_HEIGHT = 10;
/** aim_automelee_range 100 [CTL §5.5]: used as lunge range when weapon meleeChargeRange is 0. */
export const AIM_AUTOMELEE_RANGE = 100;
/** Ballistic projectile gravity: bg_gravity 800, TR_GRAVITY (T5 g_missile.cpp). ESTIMATE for T6. */
export const MISSILE_GRAVITY = 800;
/** ESTIMATE: missile ignores its owner for this long / until it leaves the owner box [NET §12.14]. */
export const MISSILE_OWNER_IGNORE_MS = 100;
/** Pickup triggers: ballistic knife r50 h100 (origin z-50); hatchet r50 h50 [SAS §3]. */
export const PICKUP_KNIFE_RADIUS = 50;
export const PICKUP_KNIFE_HEIGHT = 100;
export const PICKUP_HATCHET_RADIUS = 50;
export const PICKUP_HATCHET_HEIGHT = 50;
/** Explosive bolt beep interval: start 0.3 s, /1.2 per beep, clamp [0.08, 0.3] (_explosive_bolt.csc). */
export const BOLT_BEEP_START_MS = 300;
export const BOLT_BEEP_DIV = 1.2;
export const BOLT_BEEP_MIN_MS = 80;

// ---------------------------------------------------------------------------------------------
// Sticks and Stones rules [SAS §1, §8]
// ---------------------------------------------------------------------------------------------
export const SNS_TIME_LIMIT_MIN = 5;
export const SNS_SCORE_LIMIT = 0;
export const SNS_POINTS_CROSSBOW = 10; // pointsPerPrimaryKill
export const SNS_POINTS_SECONDARY = 10; // pointsPerSecondaryKill (ballistic knife fired, other)
export const SNS_POINTS_AXE = 5; // pointsPerPrimaryGrenadeKill
export const SNS_POINTS_MELEE = 5; // pointsPerMeleeKill
export const PLAYER_MAX_HEALTH = 100;
export const HEALTH_REGEN_DELAY_MS = 5000;
export const HEALTH_OVERLAY_CUTOFF = 0.55;
export const SPAWN_PROTECTION_MS = 3000;
export const DEATH_WATCH_MS = 1750; // defaultplayerdeathwatchtime 1.75
export const DEATH_WATCH_WATER_MS = 400; // mp_hijacked.gsc leveloverridetime 0.4
export const PREMATCH_PERIOD_MS = 15000; // ±2000 random
export const KILLCAM_MS = 4500; // camtime 2.5 + postdelay 2

// ---------------------------------------------------------------------------------------------
// View / input [CTL §4, models-anims.md §4]
// ---------------------------------------------------------------------------------------------
/** cg_fov 65: horizontal FOV at 4:3 -> vertical 51.08 deg. Keep vertical FOV fixed across aspects. */
export const CG_FOV = 65;
export const CG_FOV_VERTICAL = 51.08;
/** r_znear 4 in; viewmodel drawn in its own pass with near ~0.5 (ESTIMATE). */
export const R_ZNEAR = 4;
export const R_ZNEAR_VIEWMODEL = 0.5;
/** Pitch limits: IW player_view_pitch_up/down 85 (ESTIMATE until PM_UpdateViewAngles is read). */
export const PITCH_UP_LIMIT = 85;
export const PITCH_DOWN_LIMIT = 85;
/** Stick turn rates deg/s [CTL §4.2]. */
export const AIM_TURNRATE_YAW = 260;
export const AIM_TURNRATE_PITCH = 90;
export const AIM_TURNRATE_YAW_ADS = 90;
export const AIM_TURNRATE_PITCH_ADS = 55;
export const AIM_ACCEL_TURNRATE_LERP = 1200;
/** Look sensitivity slots 1..14, default slot 4 = 1.0 [CTL §4.1]. */
export const VIEW_SENSITIVITY_TABLE: readonly number[] = [0.4, 0.6, 0.8, 1.0, 1.2, 1.4, 1.6, 1.8, 2.0, 2.25, 2.5, 3.0, 3.5, 4.0];
export const VIEW_SENSITIVITY_DEFAULT_SLOT = 4;
/** cl_stanceHoldTime 300 ms, cl_dtpHoldTime 200 ms, use hold 250 ms (ESTIMATE) [CTL §6]. */
export const STANCE_HOLD_MS = 300;
export const DTP_HOLD_MS = 200;
export const USE_HOLD_MS = 250;
